import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { ChevronRight, MapPin, Truck, Briefcase, Loader2 } from 'lucide-react';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { formatWeekdayShort } from '../../utils/dateFormatting';

const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h, 10);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  const h12 = hr % 12 || 12;
  return `${h12}:${m}${ampm}`;
};

const userLocalDate = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Shared grouped-by-day timeline renderer used by MobileDashboard (Upcoming 7d)
 * and MobileJobs (Upcoming + History tabs + search results).
 *
 * Props:
 *   jobs           — array of job-day cards from /mobile/jobs/upcoming or /search
 *   loading        — boolean
 *   onJobClick     — (job) => void
 *   emptyMessage   — shown when !loading && jobs.length === 0
 *   compactFuture  — when true (default), only Today uses the full card; other
 *                    days use a compact layout. Set false for History tab where
 *                    every entry should be compact.
 */
export const GroupedJobTimeline = ({
  jobs,
  loading,
  onJobClick,
  emptyMessage = 'No jobs match.',
  compactFuture = true,
}) => {
  const { tenantSettings } = useMobileAuth();
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  if (!jobs || jobs.length === 0) {
    return (
      <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
        <CardContent className="py-8 text-center">
          <Briefcase className="h-12 w-12 mx-auto text-gray-600 mb-3" />
          <p className="text-[var(--m-text-secondary)]">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  const today = userLocalDate(0);
  const tomorrow = userLocalDate(1);

  const groups = {};
  for (const job of jobs) {
    const d = job.viewing_date || today;
    (groups[d] ||= []).push(job);
  }

  const formatHeader = (d) => {
    if (d === today) return 'Today';
    if (d === tomorrow) return 'Tomorrow';
    return formatWeekdayShort(d, tenantSettings?.date_format);
  };

  const sortedDays = Object.keys(groups).sort();

  return (
    <div className="space-y-4">
      {sortedDays.map((d) => {
        const isToday = d === today;
        const isFull = !compactFuture || isToday;
        const dayJobs = groups[d];

        return (
          <div key={d} data-testid={`timeline-day-${d}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-sm font-medium ${isToday ? 'text-[#ffd108]' : 'text-[var(--m-text-secondary)]'}`}>
                {formatHeader(d)}
              </h3>
              <span className="text-xs text-[var(--m-text-muted)]">
                {dayJobs.length} {dayJobs.length === 1 ? 'job' : 'jobs'}
              </span>
            </div>
            <div className="space-y-2">
              {dayJobs.map((job) => (
                <Card
                  key={`${job.id}-${job.viewing_date}`}
                  className={`bg-[var(--m-bg-card)] border-[var(--m-border)] cursor-pointer hover:border-[#ffd108] transition-colors ${isFull ? '' : 'opacity-90'}`}
                  onClick={() => onJobClick && onJobClick(job)}
                  data-testid={`timeline-job-${job.id}-${job.viewing_date}`}
                >
                  <CardContent className={isFull ? 'p-4' : 'p-3'}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-[#ffd108] font-bold text-sm">
                            {formatTime(job.start_time)}{job.end_time ? ` - ${formatTime(job.end_time)}` : ''}
                          </span>
                          {job.job_number && (
                            <span className="text-xs text-[var(--m-text-muted)]">#{job.job_number}</span>
                          )}
                          {job.status === 'sent' && (
                            <Badge variant="secondary" className="bg-amber-900 text-amber-300 text-xs">
                              Awaiting ack
                            </Badge>
                          )}
                          {job.has_docket && (
                            <Badge variant="secondary" className="bg-green-900 text-green-300 text-xs">
                              Docketed
                            </Badge>
                          )}
                          {job.status === 'invoiced' && (
                            <Badge variant="secondary" className="bg-blue-900 text-blue-300 text-xs">
                              Invoiced
                            </Badge>
                          )}
                        </div>
                        <h3 className={`text-[var(--m-text)] font-medium truncate ${isFull ? '' : 'text-sm'}`}>
                          {job.customer_name}
                        </h3>
                        <p className="text-[var(--m-text-secondary)] text-xs flex items-center gap-1 truncate">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{job.site_name}</span>
                        </p>
                        {isFull && (
                          <p className="text-[var(--m-text-muted)] text-xs flex items-center gap-1 mt-1">
                            <Truck className="h-3 w-3" />
                            {(() => {
                              const type = job.equipment_type || job.equipment_name;
                              const assetId = job.equipment_asset_id;
                              if (type && assetId) return `${type} · ${assetId}`;
                              return type || assetId || 'No asset';
                            })()}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-[var(--m-text-muted)] flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
